import { createAnonymousCommercetoolsToken } from "../auth/authFunctions";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { hasWindow } from "@lib/utils/hasWindow";

export const fetchWithLogging = async function (uri: string, options) {
    if (uri.includes("commercetools") && !uri.includes("auth.")) {
        const isServer = !hasWindow;

        if (!isServer) {
            throw new Error(
                "fetchWithLogging should only be used on the server side for commercetools requests."
            );
        }

        const authHeader =
            options.headers?.Authorization ||
            `Bearer ${(await createAnonymousCommercetoolsToken())["access_token"]}`;

        const authorization = authHeader;

        // eslint-disable-next-line no-param-reassign
        options.headers.Authorization = authorization;
    }

    const startTime = Date.now();

    let counter = 0;
    const maxRetries = 3;
    let result: Response;
    const ddHeaders = getDatadogHeaders(options.headers);
    while (counter < maxRetries) {
        result = await fetch(uri, options);
        counter++;

        await datadogLogs(ddHeaders, result, startTime, uri);

        if (result?.status < 400) {
            return result;
        }

        await dataDogLogRetry(ddHeaders, uri, result, counter);
    }

    return result;
};

interface DatadogHeaders {
    traceId: string | null;
    parentId: string | null;
    ddOrigin: string | null;
    samplingPriority: string | null;
    method: string | null;
}

const datadogLogs = async function (
    ddHeaders: DatadogHeaders,
    response: Response,
    startTime: number,
    uri: string
) {
    const endTime = Date.now();
    const duration = endTime - startTime;

    if (process.env.NEXT_PUBLIC_LOG_QUERIES === "true") {
        const message = `Dependency Request ${ddHeaders.method} ${uri} ${response.status} ${duration}ms  traceId ${ddHeaders.traceId}`;
        Logger.info(ServiceType.NEXT_API, message, {
            tag: LogTag.PERFORMANCE,
            duration: duration,
            "@mt": "Dependency Request ${method} ${uri} ${statusCode} {duration}ms ${traceId}",
            "@m": message,
            method: ddHeaders.method,
            uri,
            statusCode: response.status,
            responseLatency: duration,
            dd: {
                trace_id: ddHeaders.traceId,
                parent_id: ddHeaders.parentId,
                origin: ddHeaders.ddOrigin,
            },
            requestTime: startTime,
            requestId: ddHeaders.traceId,
            requestBody: null,
        });
    }
};

const dataDogLogRetry = async function (
    ddHeaders: DatadogHeaders,
    uri: string,
    response: Response,
    counter: number
) {
    const message = `Retrying request ${ddHeaders.method} ${uri}, status: ${response.status}, counter: ${counter}`;

    Logger.info(ServiceType.NEXT_API, message, {
        tag: LogTag.ERROR,
        "@mt": "Retrying request ${ddHeaders.method} ${uri}, status: ${statusCode}, counter: ${counter}",
        "@m": message,
        method: ddHeaders.method,
        uri,
        statusCode: response.status,
        counter,
        dd: {
            trace_id: ddHeaders.traceId,
            parent_id: ddHeaders.parentId,
            origin: ddHeaders.ddOrigin,
        },
    });
};

const getDatadogHeaders = function (headers): DatadogHeaders {
    const traceId = headers?.["x-datadog-trace-id"] || null;
    const parentId = headers?.["x-datadog-parent-id"] || null;
    const ddOrigin = headers?.["x-datadog-origin"] || null;
    const samplingPriority = headers?.["x-datadog-sampling-priority"] || null;
    const method = headers?.method;

    return {
        traceId,
        parentId,
        ddOrigin,
        samplingPriority,
        method,
    };
};

export default fetchWithLogging;
